import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import LineItemNotesInput from './LineItemNotesInput';
import { JAPANESE_LANGUAGE_CODE } from '@app/constants/commonConstants';
import { doublebyteChecker } from '@app/utils';
import { isHavingAnInvalidCharacter } from './utils';

const LineItemNotes = ({ item }) => {
  const {
    orderItemExtendAttribute: { lineItemNotes = {} },
    orderItemId
  } = item;

  const [t] = useTranslation();
  const serialNumberLabel = t('ASSET_ID_ESN_PLACEHOLDER');
  const lineItemNotesLabel = t('CAT_ORDER_LINE_ITEM_NO_PLACEHOLDER');
  const customerPartNumberLabel = t('CAT_ORDER_CPART_NUMBER_PLACEHOLDER');
  const customerItemNumberLabel = t('CAT_ORDER_CUST_ITEM_NO_PLACEHOLDER');

  const isDefaultLanguageJapanese = useSelector(
    s => s.dealer.defaultLanguage === JAPANESE_LANGUAGE_CODE
  );

  const isPCC_ShoppingCartSingleByteMessageJapan = useSelector(
    s => s.featureFlag?.PCC_ShoppingCartSingleByteMessageJapan
  );

  const isPCC_SpecialCharactersValidationFlag = useSelector(
    s => s.featureFlag?.PCC_SpecialCharactersValidationFlag
  );

  const validateDoubleByte = useMemo(
    () => isDefaultLanguageJapanese && isPCC_ShoppingCartSingleByteMessageJapan,
    [isDefaultLanguageJapanese, isPCC_ShoppingCartSingleByteMessageJapan]
  );

  const { customerPartNumber, customerSerialNumber, lineItemNo, partNote } =
    lineItemNotes;

  const [lineItemValues, setLineItemValues] = useState({
    serialNumber: customerSerialNumber || '',
    lineItemNote: partNote || '',
    customerPartNumber: customerPartNumber || '',
    customerItemNumber: lineItemNo || ''
  });

  const handleSetItemFieldValue = (value, fieldName) => {
    setLineItemValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const errorMessages = useMemo(() => {
    const handleErrorMessageByFieldName = fieldName => {
      if (fieldName === 'lineItemNote') {
        return t('REQUEST_CANNOT_BE_PROCESSED');
      }
      if (fieldName === 'customerPartNumber') {
        return t('INVALID_PART_NUMBER_NEW');
      }
      if (fieldName === 'customerItemNumber') {
        return t('INVALID_ITEM_NUMBER');
      }
      return '';
    };

    const handleErrorMessageValidator = (value, fieldName) => {
      if (
        isPCC_SpecialCharactersValidationFlag &&
        isHavingAnInvalidCharacter(value)
      ) {
        return handleErrorMessageByFieldName(fieldName);
      }

      if (validateDoubleByte && doublebyteChecker(value)) {
        return t('ERROR_SINGLE_BYTE_REQUIRED');
      }
      return '';
    };

    return {
      serialNumber: handleErrorMessageValidator(
        lineItemValues.serialNumber,
        'serialNumber'
      ),
      lineItemNotes: handleErrorMessageValidator(
        lineItemValues.lineItemNote,
        'lineItemNote'
      ),
      customerPartNumber: handleErrorMessageValidator(
        lineItemValues.customerPartNumber,
        'customerPartNumber'
      ),
      customerItemNumber: handleErrorMessageValidator(
        lineItemValues.customerItemNumber,
        'customerItemNumber'
      )
    };
  }, [
    isPCC_SpecialCharactersValidationFlag,
    lineItemValues.customerItemNumber,
    lineItemValues.customerPartNumber,
    lineItemValues.lineItemNote,
    lineItemValues.serialNumber,
    t,
    validateDoubleByte
  ]);

  return (
    <div className="py-2 px-3">
      <div className="d-flex justify-content-between flex-lg-nowrap flex-wrap d-print-none">
        <LineItemNotesInput
          label={serialNumberLabel}
          value={customerSerialNumber}
          orderItemId={orderItemId}
          type="serialNumber"
          maxLength="20"
          datacsMask
          errorMessage={errorMessages.serialNumber}
          onChange={e => {
            const inputValue = e.target.value;
            handleSetItemFieldValue(inputValue, 'serialNumber');
          }}
        />
        <LineItemNotesInput
          label={lineItemNotesLabel}
          value={partNote}
          id={`${orderItemId}-${lineItemNotesLabel}`}
          orderItemId={orderItemId}
          type="lineItemNote"
          maxLength="50"
          errorMessage={errorMessages.lineItemNotes}
          onChange={e => {
            const inputValue = e.target.value;
            handleSetItemFieldValue(inputValue, 'lineItemNote');
          }}
        />
        <LineItemNotesInput
          label={customerPartNumberLabel}
          value={customerPartNumber}
          orderItemId={orderItemId}
          type="customerPartNumber"
          maxLength="20"
          errorMessage={errorMessages.customerPartNumber}
          onChange={e => {
            const inputValue = e.target.value;
            handleSetItemFieldValue(inputValue, 'customerPartNumber');
          }}
        />
        <LineItemNotesInput
          label={customerItemNumberLabel}
          value={lineItemNo}
          orderItemId={orderItemId}
          type="customerItemNumber"
          maxLength="5"
          errorMessage={errorMessages.customerItemNumber}
          onChange={e => {
            const inputValue = e.target.value;
            handleSetItemFieldValue(inputValue, 'customerItemNumber');
          }}
        />
      </div>
    </div>
  );
};

LineItemNotes.propTypes = {
  item: PropTypes.shape({
    orderItemExtendAttribute: PropTypes.shape({
      lineItemNotes: PropTypes.shape({
        customerSerialNumber: PropTypes.string,
        customerPartNumber: PropTypes.string,
        lineItemNo: PropTypes.string,
        partNote: PropTypes.string
      })
    }),
    orderItemId: PropTypes.string
  })
};

export default LineItemNotes;
